import * as React from 'react'
import clsx from 'clsx'
import { SxProps, sx as sxFunc } from '../sx'
import styles from './index.module.scss'
import { capitalize } from '../../utils'

export interface CircularProgressClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `variant="determinate"`. */
  determinate: string
  /** Styles applied to the root element if `variant="indeterminate"`. */
  indeterminate: string
  /** Styles applied to the root element if `color="primary"`. */
  colorPrimary: string
  /** Styles applied to the root element if `color="secondary"`. */
  colorSecondary: string
  /** Styles applied to the svg element. */
  svg: string
  /** Styles applied to the `circle` svg path. */
  circle: string
  /** Styles applied to the `circle` svg path if `variant="determinate"`. */
  circleDeterminate: string
  /** Styles applied to the `circle` svg path if `variant="indeterminate"`. */
  circleIndeterminate: string
  /** Styles applied to the `circle` svg path if `disableShrink={true}`. */
  circleDisableShrink: string
}

export interface CircularProgressProps
  extends Partial<React.HTMLAttributes<HTMLSpanElement>> {
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<CircularProgressClasses>
  /**
   * The color of the component.
   * @default 'primary'
   */
  color?:
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
    | 'inherit'
  /**
   * If `true`, the shrink animation is disabled.
   * This only works if variant is `indeterminate`.
   * @default false
   */
  disableShrink?: boolean
  /**
   * The size of the component.
   * If using a number, the pixel unit is assumed.
   * If using a string, you need to provide the CSS unit, e.g '3rem'.
   * @default 40
   */
  size?: number | string
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The thickness of the circle.
   * @default 3.6
   */
  thickness?: number
  /**
   * The value of the progress indicator for the determinate variant.
   * Value between 0 and 100.
   * @default 0
   */
  value?: number
  /**
   * The variant to use.
   * Use indeterminate when there is no progress value.
   * @default 'indeterminate'
   */
  variant?: 'determinate' | 'indeterminate'
}

const SIZE = 44

/**
 * ## ARIA
 *
 * If the progress bar is describing the loading progress of a particular region of a page,
 * you should use `aria-describedby` to point to the progress bar, and set the `aria-busy`
 * attribute to `true` on that region until it has finished loading.
 */
export const CircularProgress = React.forwardRef<
  HTMLSpanElement,
  CircularProgressProps
>(function CircularProgress(props, ref) {
  const {
    className,
    classes,
    color = 'primary',
    disableShrink = false,
    size = 40,
    style,
    thickness = 1,
    value = 0,
    variant = 'indeterminate',
    sx,
    ...other
  } = props

  const circleStyle: any = {}
  const rootStyle: any = {}
  const rootProps: any = {}

  if (variant === 'determinate') {
    const circumference = 2 * Math.PI * ((SIZE - thickness) / 2)
    circleStyle.strokeDasharray = circumference.toFixed(3)
    rootProps['aria-valuenow'] = Math.round(value)
    circleStyle.strokeDashoffset = `${(
      ((100 - value) / 100) *
      circumference
    ).toFixed(3)}px`
    rootStyle.transform = 'rotate(-90deg)'
  }

  return (
    <span
      className={clsx(
        styles.root,
        classes?.root,
        styles[`variant-${variant}`],
        classes?.[variant],
        styles[`color-${color}`],
        className,
        sxFunc(sx)
      )}
      style={{ width: size, height: size, ...rootStyle, ...style }}
      ref={ref}
      role="progressbar"
      {...rootProps}
      {...other}
    >
      <svg
        className={clsx(styles.circularProgressSvg, classes?.svg)}
        viewBox={`${SIZE / 2} ${SIZE / 2} ${SIZE} ${SIZE}`}
      >
        <circle
          className={clsx(
            styles.circularProgressCircle,
            styles[`variant-${variant}`],
            // @ts-ignore
            classes?.[`circle${capitalize(variant)}`],
            !disableShrink && [styles['notDisableShrink']],
            disableShrink && classes?.circleDisableShrink,
            classes?.circle
          )}
          style={circleStyle}
          cx={SIZE}
          cy={SIZE}
          r={(SIZE - thickness) / 2}
          fill="none"
          strokeWidth={thickness}
          strokeLinecap="round"
        />
        <circle
          className={clsx(
            styles.circularProgressBgCircle,
            styles[`variant-${variant}`]
          )}
          style={circleStyle}
          cx={SIZE}
          cy={SIZE}
          r={(SIZE - thickness) / 2}
          fill="none"
          strokeWidth={thickness}
        />
      </svg>
    </span>
  )
})
